import React, { useEffect, useRef, useState } from "react";
import { Mic, Phone, PhoneOff, ShieldCheck, Volume2 } from "lucide-react";
import { Button } from "../ui/button";
import { Reveal, SectionHeading } from "../Reveal";
import { demoScript, contactInfo } from "../../mock";

const DemoSection = () => {
  const [live, setLive] = useState(false);
  const [lines, setLines] = useState([]);
  const timers = useRef([]);
  const transcriptRef = useRef(null);

  const start = () => {
    setLive(true);
    setLines([]);
    demoScript.forEach((line, i) => {
      timers.current.push(setTimeout(() => setLines((prev) => [...prev, line]), 900 * (i + 1)));
    });
  };

  const stop = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setLive(false);
  };

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: "smooth" });
  }, [lines]);

  return (
    <section id="demo" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="Interactive Demo"
          title={<>Experience the Arkision voice agent</>}
          sub="Watch a simulated patient call — the agent greets in Arabic, switches to English and Hindi, books the appointment and confirms on WhatsApp."
        />

        <div className="grid items-stretch gap-6 lg:grid-cols-5">
          {/* Call card */}
          <Reveal className="lg:col-span-3">
            <div className="card-dark flex h-full flex-col rounded-3xl p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-full ${live ? "bg-cyan-400/20" : "bg-white/5"}`}>
                    <Volume2 className={`h-5 w-5 ${live ? "text-cyan-300" : "text-slate-400"}`} />
                  </div>
                  <div>
                    <div className="font-display text-sm font-semibold text-white">Arkision Voice Agent</div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-400 blink-dot" : "bg-slate-600"}`} />
                      {live ? "Live call · City Hospital" : "Ready to demo"}
                    </div>
                  </div>
                </div>
                {live && (
                  <div className="flex h-8 items-end gap-[3px]">
                    {Array.from({ length: 14 }).map((_, i) => (
                      <span key={i} className="eq-bar w-[3px] rounded-full bg-cyan-400" style={{ animationDelay: `${i * 0.09}s` }} />
                    ))}
                  </div>
                )}
              </div>

              {/* transcript */}
              <div ref={transcriptRef} className="mt-6 h-64 flex-1 space-y-3 overflow-y-auto rounded-2xl border border-white/5 bg-black/30 p-4">
                {lines.length === 0 && (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <Mic className="h-8 w-8 text-slate-600" />
                    <p className="mt-3 max-w-xs text-sm text-slate-500">Press the mic to start a simulated patient call with live transcription.</p>
                  </div>
                )}
                {lines.map((l, i) => (
                  <div key={i} className={`flex ${l.speaker === "agent" ? "justify-start" : "justify-end"}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        l.speaker === "agent"
                          ? "rounded-bl-sm border border-cyan-400/20 bg-cyan-400/10 text-cyan-50"
                          : "rounded-br-sm bg-white/10 text-slate-100"
                      }`}
                    >
                      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-widest opacity-60">
                        {l.speaker === "agent" ? "Arkision Agent" : "Patient"} · {l.lang}
                      </span>
                      <span className={/[\u0600-\u06FF]/.test(l.text) ? "font-arabic block text-right" : ""}>{l.text}</span>
                      {l.en && <span className="mt-1 block text-xs italic opacity-60">{l.en}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* controls */}
              <div className="mt-6 flex items-center justify-center gap-4">
                {!live ? (
                  <Button
                    onClick={start}
                    size="lg"
                    className="rounded-full bg-cyan-400 px-8 font-semibold text-slate-950 transition-colors hover:bg-cyan-300"
                  >
                    <Mic className="mr-2 h-4 w-4" /> Start Demo Call
                  </Button>
                ) : (
                  <Button onClick={stop} size="lg" variant="outline" className="rounded-full border-red-400/40 bg-red-400/10 px-8 font-semibold text-red-300 hover:bg-red-400/20 hover:text-red-200">
                    <PhoneOff className="mr-2 h-4 w-4" /> End Call
                  </Button>
                )}
              </div>
            </div>
          </Reveal>

          {/* Side cards */}
          <div className="flex flex-col gap-6 lg:col-span-2">
            <Reveal delay={150}>
              <div className="card-dark card-dark-hover rounded-3xl p-7">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-cyan-400/10">
                  <Phone className="h-5 w-5 text-cyan-300" />
                </div>
                <h3 className="font-display mt-4 text-lg font-semibold text-white">Prefer a quick call?</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  Talk to our team about a live voice-agent demo for your hospital.
                </p>
                <div className="mt-4 space-y-2.5">
                  <a href={`tel:${contactInfo.indiaOffice.phone.replace(/\s/g, "")}`} className="flex items-baseline gap-2 transition-colors">
                    <span className="text-sm font-medium text-slate-400">India -</span>
                    <span className="font-display text-lg font-bold text-cyan-300 transition-colors hover:text-cyan-200">{contactInfo.indiaOffice.phone}</span>
                  </a>
                  <a href={`tel:${contactInfo.gccPartner.phone.replace(/\s/g, "")}`} className="flex items-baseline gap-2 transition-colors">
                    <span className="text-sm font-medium text-slate-400">GCC&nbsp;&nbsp;&nbsp;&nbsp;-</span>
                    <span className="font-display text-lg font-bold text-cyan-300 transition-colors hover:text-cyan-200">{contactInfo.gccPartner.phone}</span>
                  </a>
                </div>
                <span className="mt-2 block text-xs text-slate-500">Available for demo &amp; sales questions</span>
              </div>
            </Reveal>

            <Reveal delay={250}>
              <div className="card-dark rounded-3xl p-7">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-400/10">
                  <ShieldCheck className="h-5 w-5 text-emerald-300" />
                </div>
                <h3 className="font-display mt-4 text-lg font-semibold text-white">Enterprise-grade privacy</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  This demo is a simulation. In production, all patient data remains inside your country —
                  on your private cloud or on-premises infrastructure.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
